import { KeyValueStorageError } from "@validereinc/utilities";
import { nanoid } from "nanoid";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type SetStateAction,
} from "react";
import { usePrevious } from "./usePrevious";

/**
 * State that syncs with local storage.
 *
 * Credits to Josh Comeau for inspiration, but note, this has been greatly
 * enhanced from the original idea.
 * @param defaultValue the first value to store. similar concept to useState.
 * @param key the key to store the value under in local storage.
 * @param parse optionally provide an override to the parsing that needs to happen when the value from local storage is read. default is JSON.parse().
 * @returns a tuple: the value and the value setter. similar in concept to base useState.
 */
export function useStickyState<T>(
  defaultValue: T,
  key: string,
  parser: (valueFromLocalStorage: string) => T = (value) =>
    JSON.parse(value) as T
): [T, (newValueSetter: SetStateAction<T>) => void] {
  const instanceId = useRef(nanoid(4));
  const oldKey = usePrevious(key);

  const parseStorageValue = (val: string | null): T => {
    try {
      return val !== null ? parser(val) : defaultValue;
    } catch (err: unknown) {
      console.error(err);

      if (
        (err as DOMException)?.name === "InvalidCharacterError" ||
        (err as SyntaxError)?.name === "SyntaxError"
      ) {
        console.warn(
          new KeyValueStorageError(
            "Sticky state was not JSON parse-able, falling back to default value",
            {
              props: {
                value: val,
                defaultValue,
                key,
              },
              cause: err,
              getExplanation: () =>
                "Cache could not be understood. Reverting to default configuration.",
            }
          )
        );
      }

      // return the default value, even if JSON parsing fails
      return defaultValue;
    }
  };
  const getFromStorage = () =>
    parseStorageValue(window.localStorage.getItem(key));
  const [value, setValue] = useState<T>(getFromStorage);

  const setValueWithDispatchEvt = useCallback(
    (newValueSetter: SetStateAction<T>) => {
      const existingValue = window.localStorage.getItem(key);

      // always let React know about the update
      setValue((existingValueInState) => {
        let computedNewValue;

        if (newValueSetter instanceof Function) {
          computedNewValue = newValueSetter(existingValueInState);
        } else {
          computedNewValue = newValueSetter;
        }

        const stringifiedNewValue = JSON.stringify(computedNewValue);

        // only update storage if the new value is different
        if (existingValue !== stringifiedNewValue)
          window.localStorage.setItem(key, stringifiedNewValue);
        /**
         * storage evt is not fired in the window that made the change, so we
         * have to manually fire it so other instances of this hook bound to the
         * same storage key know about the update. note, we'll be ignoring this
         * duplicate storage event in this instance itself in the effect below
         * since we're updating React state here and syncing from evt is not
         * needed!
         * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/Window/storage_event}
         */
        window.dispatchEvent(
          new StorageEvent("storage", {
            key,
            newValue: stringifiedNewValue,
            oldValue: existingValue,
            storageArea: window.localStorage,
            url: `self:instance:${instanceId.current}`,
          })
        );

        return computedNewValue;
      });
    },
    [key]
  );

  // subscribe to state update notifications and update the state
  useEffect(() => {
    const handleStorageEvent = (ev: StorageEvent) => {
      // don't do anything with events from this instance, since that's unnecessary syncing work!
      if (ev.url === `self:instance:${instanceId.current}`) return;

      if (
        ev.key !== key ||
        ev.oldValue === ev.newValue ||
        ev.storageArea !== window.localStorage
      ) {
        return;
      }

      // re-sync value in storage with the value in state
      setValue((existingValue) =>
        ev.newValue === JSON.stringify(existingValue)
          ? existingValue
          : parseStorageValue(ev.newValue)
      );
    };

    window.addEventListener("storage", handleStorageEvent);

    return () => window.removeEventListener("storage", handleStorageEvent);
  }, [key]);

  // when key changes update the state
  useEffect(() => {
    // if they new key is the same as the old key
    if (oldKey === key) return;

    const oldValue = window.localStorage.getItem(key);

    // don't sync with storage if the value is already up-to-date
    if (oldValue === JSON.stringify(value)) return;

    setValue(getFromStorage());
  }, [oldKey, key, value]);

  return [value, setValueWithDispatchEvt];
}
